import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API } from "../../../config/api.js";
import { get, patch } from "../../../lib/http.js";
import { AD } from "../../adminUi.js";

function storedRole() {
  try {
    return String(JSON.parse(localStorage.getItem("bb_user") || "null")?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

function eventList(response) {
  if (Array.isArray(response)) return response;
  return response?.events || response?.requests || response?.data || [];
}

export default function AdminApprovalRequests() {
  const nav = useNavigate();
  const isSuperadmin = storedRole() === "superadmin";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [justApproved, setJustApproved] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvents(eventList(await get(API.events.pendingApprovals)));
    } catch (error) {
      toast.error(error.message || "Could not load approval requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperadmin) load();
  }, [isSuperadmin, load]);

  const approve = async (event) => {
    const id = event._id || event.id;
    setActingId(id);
    try {
      await patch(API.events.approve(id), {});
      setEvents((current) => current.filter((item) => (item._id || item.id) !== id));
      setJustApproved({ id, name: event.name || "Event" });
      toast.success(`“${event.name || "Event"}” approved — now in Approved Events.`);
    } catch (error) {
      toast.error(error.message || "Approval failed.");
    } finally {
      setActingId("");
    }
  };

  const reject = async () => {
    const cleanReason = reason.trim();
    if (!cleanReason) {
      toast.warn("Add a rejection reason for the event owner.");
      return;
    }
    const id = rejecting._id || rejecting.id;
    setActingId(id);
    try {
      await patch(API.events.reject(id), { rejectionReason: cleanReason });
      setEvents((current) => current.filter((item) => (item._id || item.id) !== id));
      setRejecting(null);
      setReason("");
      toast.success("Event rejected and feedback recorded.");
    } catch (error) {
      toast.error(error.message || "Rejection failed.");
    } finally {
      setActingId("");
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-950">
        <h1 className="text-xl font-bold">Superadmin access required</h1>
        <p className="mt-2 text-sm">Only superadmins can review event approval requests.</p>
        <Link to="/admin/events" className="mt-4 inline-flex font-semibold text-amber-800 underline">
          Back to events
        </Link>
      </div>
    );
  }

  return (
    <div className={AD.page}>
      <div className={AD.headerRow}>
        <div>
          <h1 className={AD.title}>Approval Requests</h1>
          <p className={AD.subtitle}>
            Review organiser event submissions. After approve, they move to{" "}
            <Link to="/admin/events?status=approved" className="font-semibold text-amber-800 underline">
              Approved Events
            </Link>
            .
          </p>
        </div>
        <button type="button" onClick={load} className={AD.btnSecondary}>Refresh</button>
      </div>

      {justApproved && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-950">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold">“{justApproved.name}” approved</p>
              <p className="mt-1 text-xs text-emerald-800">
                You can view full details or edit this event anytime from Approved Events.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => nav(`/admin/events/${justApproved.id}`)}
                className={AD.btnSecondary}
              >
                View details
              </button>
              <button
                type="button"
                onClick={() => nav(`/admin/events/edit/${justApproved.id}`)}
                className={AD.btnPrimary}
              >
                Edit event
              </button>
              <button
                type="button"
                onClick={() => nav("/admin/events?status=approved")}
                className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
              >
                All approved
              </button>
              <button
                type="button"
                onClick={() => setJustApproved(null)}
                className="rounded-xl px-3 py-2 text-sm font-semibold text-emerald-800/70 hover:bg-emerald-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="h-48 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
          <div className="text-3xl">✓</div>
          <h2 className="mt-2 font-bold text-slate-900">Queue is clear</h2>
          <p className="mt-1 text-sm text-slate-500">There are no events waiting for approval.</p>
          <Link
            to="/admin/events?status=approved"
            className="mt-4 inline-flex rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-slate-900 hover:bg-amber-500"
          >
            Go to Approved Events
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {events.map((event) => {
            const id = event._id || event.id;
            const owner = event.createdBy;
            return (
              <article key={id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex gap-4 p-4">
                  <div className="h-24 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                    {event.imageUrl ? (
                      <img src={event.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                      Pending approval
                    </span>
                    <h2 className="mt-2 truncate text-lg font-bold text-slate-900">
                      {event.name || "Untitled event"}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {[event.category, event.venue?.city].filter(Boolean).join(" · ") ||
                        "Event details submitted"}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      By {owner?.name || owner?.email || owner || "Member"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-slate-50 px-4 py-3">
                  <Link to={`/admin/events/${id}`} className={AD.btnSecondary}>
                    Review details
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setRejecting(event);
                      setReason("");
                    }}
                    className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => approve(event)}
                    disabled={actingId === id}
                    className={`${AD.btnPrimary} disabled:opacity-50`}
                  >
                    {actingId === id ? "Approving…" : "Approve"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {rejecting && (
        <div
          className="fixed inset-0 z-[200] grid place-items-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-slate-900">
              Reject “{rejecting.name || "event"}”?
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              The owner will see this reason and can update their listing.
            </p>
            <label className="mt-5 block text-sm font-semibold text-slate-700">
              Rejection reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={4}
                autoFocus
                className="mt-2 w-full rounded-xl border border-slate-300 p-3 font-normal outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                placeholder="Explain what needs to be corrected…"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setRejecting(null)} className={AD.btnSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={reject}
                disabled={!!actingId}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                Reject event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
